import { useState, useCallback, useContext, FC, createContext } from 'react';
import { useToast } from '@chakra-ui/react';
import auth from '../apis/auth';
import cms from '../apis/cms';

type ResetPasswordType = {
  password: string;
  passwordConfirmation: string;
  token: string;
  email: string;
};

interface IAuthContext {
  permissions: string[];
  user: any | null;
  nonEditablePages: string[] | undefined;
  updateNonEditablePages(pagesArr: string[]): Promise<void>;
  signIn(data: { email: string; password: string }): Promise<void>;
  signOut(): void;
  sendEmail(data: { email: string }): Promise<boolean>;
  resetPassword(data: ResetPasswordType): Promise<any>;
  getToken(): string | null;
}

const AuthContext = createContext<IAuthContext>({} as IAuthContext);

export const AuthProvider: FC = ({ children }) => {
  const [permissions, setPermissions] = useState<string[]>(() => {
    const storagePermissions = localStorage.getItem('@Internal:permissions');
    if (!storagePermissions) return [];
    return JSON.parse(storagePermissions);
  });

  const [nonEditablePages, setNonEditablePages] = useState<string[]>(() => {
    const storageNonEditablePages = localStorage.getItem(
      '@Internal:nonEditablePages',
    );
    if (!storageNonEditablePages) return ['home'];

    return JSON.parse(storageNonEditablePages);
  });

  const [user, setUser] = useState<any | null>(() => {
    const storageUser = localStorage.getItem('@Internal:user');
    if (!storageUser) return null;
    return JSON.parse(storageUser);
  });

  const storageToken = localStorage.getItem('@Internal:token');

  if (storageToken && !auth.defaults.headers.common.authorization) {
    auth.defaults.headers.common.authorization = `Bearer ${storageToken}`;
  }

  if (storageToken && !cms.rest.defaults.headers.common.authorization) {
    cms.rest.defaults.headers.common.authorization = `Bearer ${storageToken}`;
  }

  const sendEmail = useCallback(async (data: { email: string }) => {
    try {
      await auth.post<any>('auth/forgot-password', data);
      return true;
    } catch (err: any) {
      throw Error(
        err?.response?.data?.message ||
          err?.message ||
          'Tuvimos un error al enviar el correo electrónico',
      );
    }
  }, []);

  const resetPassword = useCallback(async (data: ResetPasswordType) => {
    try {
      await auth.post<any>('auth/reset-forgoted-password', data);
    } catch (err: any) {
      throw Error(
        err?.response?.data?.message ||
          err?.message ||
          'Tuvimos un error al cambiar tu contraseña',
      );
    }
  }, []);

  const signIn = useCallback(
    async (data: { email: string; password: string }) => {
      const { data: authData } = await auth.post<any>('auth/login', data);
      const { access_token, refresh_token } = authData;

      auth.defaults.headers.common.authorization = `Bearer ${access_token}`;
      cms.rest.defaults.headers.common.authorization = `Bearer ${access_token}`;

      const { data: apiUser } = await auth.get<any>('users/me');

      const { data: configs } = await cms.rest.get<any>('/general-config');

      const nonEditablePagesArr = configs?.non_editable_pages?.split(',');

      if (nonEditablePagesArr?.length > 0) {
        localStorage.setItem(
          '@Internal:nonEditablePages',
          JSON.stringify(nonEditablePagesArr),
        );
        setNonEditablePages([...nonEditablePagesArr, 'home']);
      }

      const hasPermission = apiUser?.roles.some((role: string) => {
        return role === 'admin' || role === 'sysadmin' || role === 'editor';
      });

      if (apiUser?.permissions.length <= 0 && !hasPermission) {
        throw Error('Usuario sin permiso');
      }
      setPermissions(apiUser?.permissions || []);
      setUser(apiUser);
      localStorage.setItem('@Internal:token', access_token);
      localStorage.setItem('@Internal:refresh_token', refresh_token);
      localStorage.setItem('@Internal:user', JSON.stringify(apiUser));
      localStorage.setItem(
        '@Internal:permissions',
        JSON.stringify(apiUser?.permissions || []),
      );
    },
    [],
  );

  const toast = useToast();

  const updateNonEditablePages = useCallback(async (pagesArr: string[]) => {
    try {
      await cms.rest.post<any>(`general-config/`, {
        non_editable_pages: String(pagesArr) ?? null,
      });
      localStorage.setItem(
        '@Internal:nonEditablePages',
        JSON.stringify(pagesArr),
      );
      setNonEditablePages([...pagesArr, 'home']);
    } catch (e) {
      toast({
        title: 'Erro',
        description: 'Ocurrió un error al actualizar este dato',
        status: 'error',
        position: 'bottom-right',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = useCallback(async () => {
    await auth.post('auth/logout');
    localStorage.clear();
    delete auth.defaults.headers.common.authorization;
    delete cms.rest.defaults.headers.common.authorization;
    setUser(null);
    setPermissions([]);
  }, []);

  const getToken = useCallback(() => {
    return localStorage.getItem('@Internal:token');
  }, []);

  return (
    <AuthContext.Provider
      value={{
        signIn,
        nonEditablePages,
        updateNonEditablePages,
        resetPassword,
        sendEmail,
        signOut,
        getToken,
        permissions,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default function useAuth(): IAuthContext {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse junto con AuthProvider');
  }
  return context;
}
